// src/lib/footprint-editor/symbols.js
//
// A template's display symbol (SY3; the column is 166's, the drawing the
// stencil master's own, design SY1): what the FootprintEditor draws over a
// footprint so a frac pump looks like one. DISPLAY ONLY: nothing here is
// measured, snapped to, hit, checked, saved or exported. The footprint stays
// the geometry everything else reads.
//
// ── THE FRAME ───────────────────────────────────────────────────────────────
//
// A symbol is stored in the footprint's own local feet (format 1:
// {format, paths: [{class, closed, heavy, pts}]}). It is placed exactly as
// the footprint is (geometry.js localRings): scaled by footprintBase's sx and
// sy, which come from the FOOTPRINT's extent and the feature's length/width
// override, then turned by toGrid and moved to the anchor in the frame.
// Never by the symbol's own bbox: 151's pad_plan_feature_geom scales by its
// input's bbox, and 7 of v4's 57 drawings do not fill their footprint box
// (two are drawn past it), so reusing it would stretch them (the 151 trap).
//
// ── THE GATES ───────────────────────────────────────────────────────────────
//
// On the map, symbols draw from zoom 19 (SYMBOL_MIN_ZOOM): at z18 a pump is
// about 5 px wide and its drawing is noise; below 19 the footprint's outline
// alone. Paper view draws them at about 40 ft to the inch or finer
// (PAPER_SYMBOL_FT_PER_IN), on a 96 px CSS inch: paperSymbolMinZoom(lat) is
// the zoom at which one screen inch is 40 ft at the plan's latitude (18.99
// at Midland, so the two gates meet there).
import { M_PER_FT, ftToM } from "../units.js";
import { footprintBase, toGrid } from "./geometry.js";

export const SYMBOL_MIN_ZOOM = 19;
export const PAPER_SYMBOL_FT_PER_IN = 40;
export const CSS_PX_PER_IN = 96;
export const SYMBOL_COLORS = Object.freeze({ hp: "#C0392B", lp: "#3A7BD5" });
export const SYMBOL_FILL_OPACITY = 0.25;
export const SYMBOL_LINE_WIDTH = Object.freeze({ thin: 0.75, heavy: 1.75 });
const EARTH_M = 40075016.686;        // geometry.js metersPerPixel's, for 512 px tiles
const rad = Math.PI / 180;

/** The zoom at which one CSS inch on screen is `ftPerIn` feet at `lat` (MapLibre's 512 px tiles). */
export function paperSymbolMinZoom(lat, ftPerIn = PAPER_SYMBOL_FT_PER_IN) {
  return Math.log2((EARTH_M * Math.cos(lat * rad) * CSS_PX_PER_IN) / (512 * M_PER_FT * ftPerIn));
}

/** A template's symbol when it is one this file draws (format 1, paths), else null. */
export function templateSymbol(template) {
  const s = template?.symbol;
  return s && s.format === 1 && Array.isArray(s.paths) && s.paths.length ? s : null;
}

/**
 * One feature's symbol as GeoJSON in 4326, [] when it has none: a LineString
 * per path ({sym: class, heavy}), and for a closed hp or lp ring a Polygon as
 * well ({sym: class, fill: true}). The scale is the footprint's (footprintBase),
 * never the symbol's own.
 */
export function symbolFeatures(feature, template, frame) {
  if (feature?.kind !== "footprint" || !frame) return [];
  const sym = templateSymbol(template);
  const at = feature.anchor?.coordinates;
  if (!sym || !at) return [];
  const b = footprintBase(feature, template);
  if (!b) return [];
  const [ax, ay] = frame.toLocal(at);
  const rot = feature.rotation_deg ?? 0;
  const place = ([x, y]) => {
    const [de, dn] = toGrid([ftToM(x * b.sx), ftToM(y * b.sy)], rot);
    return frame.toLngLat([ax + de, ay + dn]);
  };
  const out = [];
  for (const p of sym.paths) {
    if (!Array.isArray(p?.pts) || p.pts.length < 2) continue;
    const coords = p.pts.map(place);
    const cls = p.class === "hp" || p.class === "lp" || p.class === "outline" ? p.class : "detail";
    if (p.closed && (cls === "hp" || cls === "lp") && coords.length >= 4) {
      out.push({ type: "Feature", properties: { sym: cls, fill: true }, geometry: { type: "Polygon", coordinates: [coords] } });
    }
    out.push({ type: "Feature", properties: { sym: cls, heavy: Boolean(p.heavy) }, geometry: { type: "LineString", coordinates: coords } });
  }
  return out;
}

/**
 * The symbol layers' paint for a drawing style: outline and detail in the
 * footprint's line color (the theme's ink in Paper view), hp and lp in their
 * own; heavy paths wider; closed hp/lp rings a translucent fill of their color.
 */
export function symbolPaint({ ink = null } = {}) {
  const color = ["match", ["get", "sym"], "hp", SYMBOL_COLORS.hp, "lp", SYMBOL_COLORS.lp, ink ?? "#f8fafc"];
  return {
    lineColor: color,
    lineWidth: ["case", ["==", ["get", "heavy"], true], SYMBOL_LINE_WIDTH.heavy, SYMBOL_LINE_WIDTH.thin],
    fillColor: color,
    fillOpacity: SYMBOL_FILL_OPACITY,
  };
}
